import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/features/ui/primitives/card';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { logger } from '@/features/help/utils/logger';
import { Tooltip } from '@/features/ui/components/Tooltip';

/**
 * Provider Selection Page component that replaces the modal approach
 * with a dedicated page for selecting CRM providers
 */
const ProviderSelectionModal: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedProvider, initiateOAuth } = useAuthStore();

  const providers = [
    {
      id: 'salesforce',
      name: 'Salesforce',
      logo: 'https://logo.clearbit.com/salesforce.com',
      color: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      disabled: false,
    },
    {
      id: 'zoho',
      name: 'Zoho',
      logo: 'https://logo.clearbit.com/zoho.com',
      color: 'bg-green-50 hover:bg-green-100 border-green-200',
      disabled: true,
    },
    {
      id: 'hubspot',
      name: 'HubSpot',
      logo: 'https://logo.clearbit.com/hubspot.com',
      color: 'bg-gray-50 border-gray-200',
      disabled: true,
    },
    {
      id: 'gohighlevel',
      name: 'Go High Level',
      logo: 'https://logo.clearbit.com/gohighlevel.com',
      color: 'bg-gray-50 border-gray-200',
      disabled: true,
    },
  ];

  const handleProviderSelect = async (providerId: string) => {
    logger.info('ProviderSelectionModal', 'User selected CRM provider', { providerId });
    setSelectedProvider(providerId);

    try {
      await initiateOAuth(providerId);
      // OAuth will redirect away, no need to navigate manually
    } catch (error) {
      logger.error('ProviderSelectionModal', 'Failed to initiate OAuth', { providerId, error });
      // On error, navigate back to dashboard - the loader will handle the error
      navigate('/dashboard');
    }
  };

  const renderProvider = (provider: any) => {
    const cardContent = (
      <Card
        key={provider.id}
        className={`transition-all duration-200 ${provider.color} ${
          provider.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
        onClick={provider.disabled ? undefined : () => handleProviderSelect(provider.id)}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-4">
            <img
              src={provider.logo}
              alt={`${provider.name} logo`}
              className="w-8 h-8 object-contain"
              onError={(e) => {
                // Fallback to a simple colored circle if logo fails to load
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="w-8 h-8 bg-gray-300 rounded-full hidden"></div>
            <span className="text-lg font-medium">{provider.name}</span>
          </div>
        </CardContent>
      </Card>
    );

    if (provider.disabled) {
      return (
        <Tooltip key={provider.id} text={`Coming soon!`} side="right">
          <div className="cursor-not-allowed">{cardContent}</div>
        </Tooltip>
      );
    }

    return cardContent;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Connect Your CRM</h1>
          <p className="mt-2 text-sm text-gray-600">
            Choose which CRM system you'd like to connect to manage your customer data and
            applications.
          </p>
        </div>

        <div className="space-y-4">{providers.map((provider) => renderProvider(provider))}</div>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            You can change your CRM provider later in settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProviderSelectionModal;
